import './baseline.css' // Should be able to remove when material design lite removed.
import 'antd/dist/antd.min.css' // needed for LayerTree

import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import get from 'lodash/get'
import includes from 'lodash/includes'
import toLower from 'lodash/toLower'
import trim from 'lodash/trim'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import ResourceCheck from 'data/ResourceCheck'
import LayerHeader from 'drawer/tabs/layers/panels/LayerHeader'
import BuildKeywords from 'drawer/tabs/layers/search/BuildKeywords'
import { layerValidInCurrentProfile } from 'utils/profile'

import LayerTree from './LayerTree'

export const PANEL_TITLE_DOWNLOAD = 'Download'
export const PANEL_TITLE_DRAW = 'Draw'
export const PANEL_TITLE_LEGEND = 'Legend'
export const PANEL_TITLE_OPACITY = 'Opacity'
export const PANEL_TITLE_REPORT = 'Report'
export const PANEL_TITLE_FILTER = 'Filter'
export const PANEL_TITLE_TABLE = 'Table'
export const PANEL_TITLE_ABOUT = 'About'
export const PANEL_TITLE_DEFAULT = PANEL_TITLE_LEGEND
export const ICON_COLOR_ENABLED = '#7b7b7b'
export const ICON_COLOR_DISABLED = '#afafaf'

export const SHOW_TABLE_MIN_WIDTH = 600
export const SHOW_TABLE_MIN_HEIGHT = 400

const styles = theme => ({
  root: {},
  paper: {
    margin: 0
  },
  panels: {
    margin: 10,
    padding: 0,
    paddingLeft: 6,
    paddingRight: 6,
    paddingBottom: 14,
    minWidth: 250,
    transition: 'all 0.5s'
  },
  filtered: {
    display: 'none'
  },
  notFiltered: {}
})

class LayerPanels extends React.Component {
  constructor (props) {
    super(props)

    const { titleid } = this.props

    // TODO: move this to props
    const layerConf = PoziUtils.getLayerFromTitleID(titleid)
    let keywords = layerConf?.keywords
    if (!keywords) {
      console.warn('LayerPanels - generating keywords. This should not be needed!', titleid)
      keywords = BuildKeywords(layerConf)
    }

    this.state = {
      contextMenu: null,
      expandedKeys: [],
      filter: keywords,
      selectedKeys: []
    }
  }

  async resourceCheck () {
    const { layerResourceCheck } = this.props
    if (layerResourceCheck !== '') {
      const bResourceFound = await ResourceCheck(layerResourceCheck)
      if (bResourceFound) {
        console.log('Resource found', layerResourceCheck)
      } else {
        console.log('Resource not found', layerResourceCheck)
      }
    }
  }

  // Used to set the current opening element back onto page if the one above collapsed pulling the current on off the screen.
  getPanelId (titleid, panelTitle) {
    return PoziUtils.getPoziIDFromString(titleid + '_' + panelTitle)
  }

  renderLayerTree () {
    const { titleid } = this.props

    const layerTree = (
      <LayerTree titleid={titleid} />
    )

    return layerTree
  }

  renderLayerHeader () {
    const { classes, groupid, titleid, layerPanelsOpen, bEditEnabled, bLayerEditEnabled, isLayerSelected } = this.props

    const layerComponent = (
      <LayerHeader titleid={titleid} />
    )

    return layerComponent
  }

  render () {
    const { filter } = this.state
    const { classes, currentProfile, profiles, groupid, titleid, tabLayersFilter, layerPanelsOpen, bEditEnabled, bLayerEditEnabled, printUIVisible, showInLayerControl, isLayerSelected } = this.props

    const bConfigInState = Model.getState('config', false)
    if (bConfigInState) {
      console.log('Skipping layer panels render due to config flag.')
      return null
    }

    if (!showInLayerControl) {
      return null
    }

    // console.debug('LayerPanels - Render layer', titleid, groupid, 'panels')

    let filteredClass = classes.notFiltered

    // Check for layer filter match but not when we are in print mode
    const tabLayersFilterLC = toLower(trim(tabLayersFilter))
    if (!printUIVisible && tabLayersFilterLC !== '' && !includes(filter, tabLayersFilterLC)) {
      // CSS masking for performance. https://stackoverflow.com/a/24590605/1550587
      filteredClass = classes.filtered
      // return null
    }

    if (!titleid || titleid === '') {
      return null
    }

    const layerConf = PoziUtils.getLayerFromTitleID(titleid)

    if (!layerConf) {
      console.log('Layerpanels - No layerConf', titleid, layerConf)
      return null
    }

    // Check if the layer is valid in the current profile.
    const childLayerIsValidInCurrentProfile = layerValidInCurrentProfile(titleid)
    if (!childLayerIsValidInCurrentProfile) {
      console.debug(`ChildPanels - render - layer '${titleid}' not in valid current profile: '${currentProfile}'. Ignoring.`)

      return null
    }

    const layerIsCatalogue = PoziUtils.isLayerTypeCatalogue(layerConf)
    const layerIsFlattenedCatalogue = layerIsCatalogue && layerConf.flatten === true

    const layerIsWFSCapabilities = (layerConf.type || '').match(/wfsgetcapabilities/i)
    const layerIsWMTSCapabilities = (layerConf.type || '').match(/wmtscapabilities/i)

    // Only render layer tree if it is of a non-flattened WMS-like capabilities type
    const shouldRenderLayerTree = layerIsCatalogue &&
      !layerIsFlattenedCatalogue &&
      !layerIsWFSCapabilities &&
      !layerIsWMTSCapabilities

    const layerTree = shouldRenderLayerTree ? this.renderLayerTree() : null

    // Special case: don't render layer header if the layer type is a catalogue layer
    const shouldRenderLayerHeader = !shouldRenderLayerTree && !layerIsCatalogue

    // When in print mode, only show layers that have a legend. Here we only cover
    // layer header components. The same logic is applied to the layer tree
    // in its own component.
    if (shouldRenderLayerHeader && printUIVisible) {
      const visibleLayersWithLegend = PoziUtils.getVisibleLayersWithLegend()
      // console.debug('LayerPanels - render - visibleLayersWithLegend', titleid, visibleLayersWithLegend)

      const showLayer = visibleLayersWithLegend.some(layerConf => layerConf.titleid === titleid)

      if (!showLayer) {
        // console.debug('LayerPanels - render - print mode - no legend. Not showing', titleid, showLayer)

        filteredClass = classes.filtered
      }
    }

    const layerHeader = shouldRenderLayerHeader ? this.renderLayerHeader() : null

    // console.debug('LayerPanels - render layer header?', groupid, titleid, shouldRenderLayerHeader)
    // console.debug('LayerPanels - render layer tree?', groupid, titleid, shouldRenderLayerTree)

    const markup = (
      <div className={filteredClass}>
        <div className={classes.root}>
          { layerHeader }
          { layerTree }
        </div>
      </div>
    )

    return markup
  }
}

LayerPanels.propTypes = {
  classes: PropTypes.object.isRequired,
  currentProfile: PropTypes.string,
  profiles: PropTypes.object,
  layerPanelsOpen: PropTypes.bool.isRequired,
  printUIVisible: PropTypes.bool,
  tabLayersFilter: PropTypes.string.isRequired,
  groupid: PropTypes.string.isRequired,
  titleid: PropTypes.string.isRequired,
  bEditEnabled: PropTypes.bool.isRequired,
  bLayerEditEnabled: PropTypes.bool.isRequired,
  layerResourceCheck: PropTypes.string.isRequired,
  showInLayerControl: PropTypes.bool,
  isLayerSelected: PropTypes.bool.isRequired
}

function mapStateToProps (state, ownProps) {
  const { currentProfile, printUIVisible, profiles } = state

  const layerPanelsOpen = get(state, 'layers[' + ownProps.titleid + '].panelsOpen', null)
  const showInLayerControl = get(state, 'layers[' + ownProps.titleid + '].showInLayerControl', true)
  const tabLayersFilter = get(state, 'tabLayersFilter', '')
  const bEditEnabled = get(state, 'config.layout.drawer.tabs.tools.annotations.enabled', false)
  const bLayerEditEnabled = get(state, 'layers[' + ownProps.titleid + '].edit', false)
  const layerResourceCheck = get(state, 'layers[' + ownProps.titleid + '].ResourceCheck', '')
  const isLayerSelected = get(state, 'layers[' + ownProps.titleid + '].selected', false)

  return {
    currentProfile,
    profiles,
    layerPanelsOpen,
    printUIVisible,
    showInLayerControl,
    tabLayersFilter,
    bEditEnabled,
    bLayerEditEnabled,
    layerResourceCheck,
    isLayerSelected
  }
}

const mapDispatchToProps = dispatch => {
  return {}
}

export default (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(LayerPanels)
  )
)
